import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFetchRoadAccidentAmountMutation } from "../../../../api/rtk/base/slices/objectTypes/roadAccidentsApiSlice";
import { Loader } from "../../../loaders/Loader";

export default function RoadAccidentsNumberByYearsChart() {
    const [chartData, setChartData] = useState<any>(null)
    const [fetchRoadAccidentAmount] = useFetchRoadAccidentAmountMutation()
    const [chartIsShown, setChartIsShown] = useState<boolean>(true)
    const [roadAccidentsNumberIsFetching, setRoadAccidentsNumberIsFetching] = useState<boolean>(true)

    useEffect(() => {
        getChartData()
    }, [])

    async function getChartData() {
        setRoadAccidentsNumberIsFetching(true)
        const currentYear = new Date().getFullYear();
        const lastFiveYears = Array.from({ length: 5 }, (_, index) => currentYear - index);
        let data = []
        let hasAtLeastOneFilled = false
        for (const year of lastFiveYears) {
            const promisedRoadAccidentAmount = getRoadAccidentAmount(year)
            const keys = Object.keys(promisedRoadAccidentAmount)
            const values: any = await Promise.all(Object.values(promisedRoadAccidentAmount))
            let withVictimsCount = 0
            let roadAccidentAmount = keys.reduce((acc: any, key: any, index: any) => {
                const count = values[index]?.data?.Count
                acc[key] = count
                if (key != "total") {
                    withVictimsCount += count
                }
                return acc
            }, {})
            const remaining = roadAccidentAmount.total ? roadAccidentAmount.total - withVictimsCount : 0
            roadAccidentAmount = { ...roadAccidentAmount, remaining, year }
            if (roadAccidentAmount.total) {
                hasAtLeastOneFilled = true
            }
            data.push(roadAccidentAmount)
        }
        setChartIsShown(hasAtLeastOneFilled)
        setChartData(data)
        setRoadAccidentsNumberIsFetching(false)
    }

    function getRoadAccidentAmount(year: any): any {
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;
        const roadAccidentTotalAmount: any = fetchRoadAccidentAmount({ startDate, endDate })
        const roadAccidentWithFatalitiesAmount: any = fetchRoadAccidentAmount({ startDate, endDate, victims: "WithFatalities" })
        return {
            total: roadAccidentTotalAmount,
            withFatalities: roadAccidentWithFatalitiesAmount
        }
    }

    const yearAxisFormatter = (value: any) => `${value} г.`
    const legendFormatter = (value: any) => <span className="legend-text">{value}</span>

    const chartTooltipTemplate: any = ({ payload }: any) => {
        const chartData = payload[0]?.payload
        const { withFatalities, total } = chartData ?? {}
        return (
            <div className="chart-tooltip">
                <p>Есть погибшие: <span id="with-fatalities-value">{withFatalities}</span></p>
                <p>Всего: <span id="total-value">{total}</span></p>
            </div>
        )
    }

    return (
        <>
            {roadAccidentsNumberIsFetching
                ?
                <Loader />
                :
                <>
                    {chartIsShown
                        ?
                        <ResponsiveContainer width="100%" height={357}>
                            <BarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ left: 50, right: 50, bottom: 15, top: 15 }}
                                barGap={10}
                                className="road-accidents-number-by-year-chart"
                            >
                                <defs>
                                    <linearGradient id="withFatalities">
                                        <stop offset="0%" stopColor="#3478c9" />
                                        <stop offset="100%" stopColor="#054a9b" />
                                    </linearGradient>
                                    <linearGradient id="remaining">
                                        <stop offset="0%" stopColor="#9B9B9B" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#9B9B9B" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "#999999" }} />
                                <YAxis dataKey="year" type="category" tickFormatter={yearAxisFormatter} tick={{ fill: "black", fontWeight: "600" }} />
                                <Legend formatter={legendFormatter} />
                                <Tooltip content={chartTooltipTemplate} />
                                <Bar dataKey="withFatalities" stackId="percent" fill="url(#withFatalities)" name="Есть погибшие" barSize={20} />
                                <Bar dataKey="remaining" stackId="percent" tooltipType="none" legendType="none" fill="url(#remaining)" barSize={20} label={{ fill: "black", position: 'right' }} />
                            </BarChart>
                        </ResponsiveContainer>
                        :
                        <div className="panel-no-content">
                            <span>Нет информации</span>
                        </div>
                    }
                </>
            }
        </>
    )
}
